// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import './styles.css';
import { loadDataset, searchFacilities, type Dataset } from './data';
import type { ViewId } from './types';
import type { ViewContext } from './viewContext';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossaryPopover';
import { createAbout } from './components/about';
import { createDrilldown } from './components/drilldown';
import { renderOverview } from './views/overview';
import { renderOverline } from './views/overline';
import { renderMap } from './views/map';
import { renderSectors } from './views/sectors';
import { renderCompanies } from './views/companies';
import { renderMarket } from './views/market';
import { renderGases } from './views/gases';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { escapeHtml, formatCompact } from './utils/format';
import { positionColour } from './sectors';

// Nav tabs are words only — never count badges.
const VIEWS: Array<{ id: ViewId; label: string; render: (root: HTMLElement, ctx: ViewContext) => void }> = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'overline', label: 'Over the Line', render: renderOverline },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'sectors', label: 'Sectors', render: renderSectors },
  { id: 'companies', label: 'Companies', render: renderCompanies },
  { id: 'market', label: 'Carbon Market', render: renderMarket },
  { id: 'gases', label: 'Gases', render: renderGases },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
];

const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true">
  <rect x="6" y="19" width="4.4" height="9" rx="1" fill="#94a3b8"/>
  <rect x="13.8" y="14" width="4.4" height="14" rx="1" fill="#64748b"/>
  <rect x="21.6" y="8.5" width="4.4" height="19.5" rx="1" fill="#e2562b"/>
  <path d="M5 12 L12 9.5 L19.5 6.5 L27 4" fill="none" stroke="#b23a00" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
  <circle cx="27" cy="4" r="2.1" fill="#b23a00"/>
</svg>`;

function parseHash(): { view: ViewId | null; facility: string | null } {
  const h = location.hash.replace(/^#/, '');
  if (!h) return { view: null, facility: null };
  if (h.startsWith('f=')) return { view: null, facility: h.slice('f='.length) };
  const view = VIEWS.find((v) => v.id === h)?.id ?? null;
  return { view, facility: null };
}

function shell(app: HTMLElement, data: Dataset): void {
  const { meta } = data;

  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview">${LOGO}<span>Safeguard Emitters</span><span class="brand-sub">Australia's biggest carbon emitters</span></a>
        <div class="search-wrap">
          <input class="search-input" type="search" placeholder="Search a facility or company…" aria-label="Search a facility or company" autocomplete="off" />
          <div class="search-results" role="listbox"></div>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" data-role="about" aria-label="About this site" title="About this site">?</button>
      </div>
    </header>
    <nav class="nav-tabs" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>
          Data: <a href="${meta.sources.dataset}" target="_blank" rel="noopener">Clean Energy Regulator — Safeguard baselines &amp; emissions</a>,
          ${escapeHtml(meta.reportingYear)} · state boundaries ABS ASGS 2021 (CC BY 4.0).
        </span>
        <span>
          Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </span>
      </div>
    </footer>
  `;

  const viewRoot = app.querySelector('#view-root') as HTMLElement;
  const about = createAbout(meta);
  const drill = createDrilldown(data);
  let current: ViewId = 'overview';

  const ctx: ViewContext = {
    data,
    openFacility: (slug) => drill.open(slug),
    goTo: (v) => show(v),
  };

  function show(id: ViewId, pushHash = true): void {
    const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
    drill.close();
    current = view.id;
    app.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === current)));
    viewRoot.innerHTML = '';
    try {
      view.render(viewRoot, ctx);
    } catch {
      viewRoot.innerHTML = '<div class="error-box">Something went wrong rendering this view. Try another tab.</div>';
    }
    if (pushHash) history.replaceState(null, '', `#${current}`);
    window.scrollTo({ top: 0 });
  }

  app.querySelector('.nav-inner')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) show(btn.getAttribute('data-view') as ViewId);
  });
  app.querySelector('[data-role="about"]')?.addEventListener('click', () => about.open());
  app.querySelector('.brand')?.addEventListener('click', (e) => {
    e.preventDefault();
    show('overview');
  });

  // ── Header search ──
  const input = app.querySelector('.search-input') as HTMLInputElement;
  const results = app.querySelector('.search-results') as HTMLElement;
  let hits: ReturnType<typeof searchFacilities> = [];
  let cursor = -1;
  const closeSearch = () => {
    results.innerHTML = '';
    cursor = -1;
  };
  const paintResults = () => {
    results.innerHTML = hits
      .map((f, i) => {
        return `
        <button class="search-item" data-slug="${escapeHtml(f.slug)}" role="option" aria-selected="${i === cursor}">
          <strong style="color:${positionColour(f.excess)}">${escapeHtml(f.name.length > 34 ? f.name.slice(0, 33) + '…' : f.name)}</strong>
          <span>${escapeHtml(f.company)}${f.state !== 'MULTI' ? ` · ${escapeHtml(f.state)}` : ''}</span>
          <em>${formatCompact(f.covered)} t</em>
        </button>`;
      })
      .join('');
  };
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hits = searchFacilities(data.facilities, input.value);
      cursor = -1;
      paintResults();
    }, 300);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      input.blur();
      return;
    }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      cursor = (cursor + 1) % hits.length;
      paintResults();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      cursor = (cursor - 1 + hits.length) % hits.length;
      paintResults();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = hits[cursor >= 0 ? cursor : 0];
      if (pick) {
        drill.open(pick.slug);
        input.value = '';
        closeSearch();
      }
    }
  });
  results.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-slug]');
    if (!btn) return;
    drill.open(btn.getAttribute('data-slug') as string);
    input.value = '';
    closeSearch();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('.search-wrap')) closeSearch();
  });

  // ── Routing ──
  const initial = parseHash();
  show(initial.view ?? 'overview', !initial.facility);
  if (initial.facility) drill.open(initial.facility);
  window.addEventListener('hashchange', () => {
    const { view, facility } = parseHash();
    if (facility) drill.open(facility);
    else if (view && view !== current) show(view, false);
  });
}

function renderError(app: HTMLElement, retry: () => void): void {
  app.innerHTML = `
    <main class="main-content">
      <div class="error-box">
        <strong>Could not load the data.</strong>
        <p style="margin-top:8px">Check your connection and try again.</p>
        <button type="button">Retry</button>
      </div>
    </main>`;
  app.querySelector('button')?.addEventListener('click', retry);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app') as HTMLElement;
  app.innerHTML = `
    <main class="main-content">
      <div class="loading">Loading Australia's Safeguard emissions data…</div>
      <div class="skeleton"></div>
    </main>`;
  initTooltip();
  initGlossary();
  try {
    const data = await loadDataset();
    shell(app, data);
  } catch {
    renderError(app, () => {
      void boot();
    });
  }
}

void boot();

export type { Dataset };
